"use client";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Phone, Mail, MapPin, Instagram, Facebook, Twitter, Linkedin } from "lucide-react";
import React, { useCallback } from "react";

export default function Footer() {
  const router = useRouter();

  const handleNavigation = useCallback((path) => {
    router.push(path);
  }, [router]);

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-gray-50 to-gray-100 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">

            {/* Brand Section - Enlarged */}
            <div className="lg:col-span-1">
              <div
                className="cursor-pointer inline-block mb-6"
                onClick={() => router.push("/")}
                aria-label="Go to homepage"
                tabIndex={0}
                role="button"
              >
                <Image
                  src="/images/sortkaro_login_logo.png"
                  width={220}
                  height={280}
                  alt="SortKaro Logo"
                  priority
                  className="h-20 w-auto"
                />
              </div>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Your trusted partner for professional home services in India. Quality service, verified professionals, hassle-free booking.
              </p>

              {/* Social Media Icons - Larger */}
              <div className="flex items-center gap-4">
                <a
                  href="https://instagram.com/sortkaro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-[#00489b] hover:text-white hover:border-[#00489b] transition-all duration-300 shadow-sm hover:shadow-md"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://facebook.com/sortkaro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-[#00489b] hover:text-white hover:border-[#00489b] transition-all duration-300 shadow-sm hover:shadow-md"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://twitter.com/sortkaro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-[#00489b] hover:text-white hover:border-[#00489b] transition-all duration-300 shadow-sm hover:shadow-md"
                  aria-label="Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href="https://linkedin.com/company/sortkaro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-[#00489b] hover:text-white hover:border-[#00489b] transition-all duration-300 shadow-sm hover:shadow-md"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Quick Links - Enlarged */}
            <div>
              <h3 className="text-gray-900 font-bold text-lg mb-6">Quick Links</h3>
              <nav>
                <ul className="space-y-4">
                  {[
                    { text: "Home", path: "/" },
                    // { text: "Services", path: "/#services" },
                    { text: "Blog", path: "/blog-sortkaro" },
                    { text: "About Us", path: "/about-sortkaro" },
                    { text: "Contact", path: "/contact-sortkaro" },
                  ].map(({ text, path }) => (
                    <li key={text}>
                      <button
                        onClick={() => handleNavigation(path)}
                        className="text-gray-600 text-base hover:text-[#00489b] hover:translate-x-1 transition-all duration-200 inline-block"
                      >
                        {text}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* Support - Enlarged */}
            <div>
              <h3 className="text-gray-900 font-bold text-lg mb-6">Support</h3>
              <nav>
                <ul className="space-y-4">
                  {[
                    // { text: "FAQs", path: "/faq-sortkaro" },
                    { text: "Help Center", path: "/works-sortkaro" },
                    { text: "Privacy Policy", path: "/privacy-policy" },
                    { text: "Terms & Conditions", path: "/terms-and-conditions" },
                  ].map(({ text, path }) => (
                    <li key={text}>
                      <button
                        onClick={() => handleNavigation(path)}
                        className="text-gray-600 text-base hover:text-[#00489b] hover:translate-x-1 transition-all duration-200 inline-block"
                      >
                        {text}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* Contact Information - Enlarged */}
            <div>
              <h3 className="text-gray-900 font-bold text-lg mb-6">Contact Us</h3>
              <address className="not-italic space-y-5">
                <div className="flex items-start gap-3">
                  <MapPin className="w-6 h-6 text-[#00489b] flex-shrink-0 mt-0.5" />
                  <div className="text-gray-600 text-base">
                    <p>Gurugram, Haryana</p>
                    <p>India</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-6 h-6 text-[#00489b] flex-shrink-0 mt-0.5" />
                  <a
                    href="tel:+917700969697"
                    className="text-gray-600 text-base hover:text-[#00489b] transition-colors"
                  >
                    +91-7700969697
                  </a>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-6 h-6 text-[#00489b] flex-shrink-0 mt-0.5" />
                  <a
                    href="mailto:helpdesk@sortkaro.com"
                    className="text-gray-600 text-base hover:text-[#00489b] transition-colors break-all"
                  >
                    helpdesk@sortkaro.com
                  </a>
                </div>
              </address>

              {/* App Download Badges - Larger */}
              <div className="mt-8 space-y-4">
                <a
                  href="https://play.google.com/store/apps/details?id=com.sortkaro.user&hl=en_IN"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block transform transition-transform hover:scale-105"
                >
                  <img
                    src="/images/googleplay.png"
                    alt="Get it on Google Play"
                    className="h-12 w-auto"
                    loading="lazy"
                  />
                </a>
                <a
                  href="https://apps.apple.com/in/app/sortkaro/id6747939950"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block transform transition-transform hover:scale-105"
                >
                  <img
                    src="/images/apple.png"
                    alt="Download on the App Store"
                    className="h-12 w-auto"
                    loading="lazy"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200"></div>

        {/* Bottom Bar */}
        <div className="py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-gray-600 text-base">
                © {currentYear} SortKaro. All rights reserved.
              </p>
              <p className="text-gray-500 text-sm mt-1">
                "SortKaro" trademark is owned by Ideayatra Private Limited
              </p>
            </div>

            <div className="flex items-center gap-6 text-base text-gray-600">
              <button
                onClick={() => handleNavigation("/privacy-policy")}
                className="hover:text-[#00489b] transition-colors"
              >
                Privacy
              </button>
              <button
                onClick={() => handleNavigation("/terms-and-conditions")}
                className="hover:text-[#00489b] transition-colors"
              >
                Terms
              </button>
              <button
                onClick={() => handleNavigation("/contact-sortkaro")}
                className="hover:text-[#00489b] transition-colors"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
